
import React, { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { LeadOutcome } from './types/LeadTypes';

export const getOutcomeCircleColor = (outcome: LeadOutcome | null): string => {
  if (!outcome) return '#f3f4f6'; // default light gray

  if (outcome === 'Existing Customer') {
    return '#0EA5E9'; // blue for existing customer
  } else if (outcome.startsWith('Sale Won') || outcome.startsWith('Appointment Set')) {
    return '#22c55e'; // green-500 for positive outcomes
  } else if (outcome === 'Lead Will Call Back') {
    return '#FFC107'; // yellow for "Lead Will Call Back"
  } else if (outcome === 'Unknown' || outcome.startsWith('Call Back Requested') || outcome.startsWith('Research / Price Shopping')) {
    return '#FFC107'; // amber/yellow for pending/unknown outcomes
  } else if (outcome.startsWith('Disqualified')) {
    return '#ef4444'; // red-500 for disqualified outcomes
  } else if (outcome.startsWith('Lost')) {
    return '#9CA3AF'; // gray-400 for lost opportunities
  } else {
    return '#f3f4f6'; // default light gray
  }
};

interface OutcomeSelectProps {
  outcome: LeadOutcome | null;
  onChange: (value: LeadOutcome | null) => void;
}

const OutcomeSelect: React.FC<OutcomeSelectProps> = ({ outcome, onChange }) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const options: LeadOutcome[] = [
    'Existing Customer',
    'Call Back Requested',
    'Lead Will Call Back',
    'Research / Price Shopping',
    'Unknown',
    'Appointment Set',
    'Sale Won',
    'Lost (job too small)',
    'Lost (price too high)',
    'Lost (service timeline)',
    'Lost (service not provided)',
    'Disqualified (employment inquiry)',
    'Disqualified (looking for competitors)',
    'Disqualified (not sales related)',
    'Disqualified (spam/telemarketer)',
    'Disqualified (out of service area)',
    'Disqualified (wrong industry)',
    'Disqualified (wrong number)'
  ];

  const getDisplayText = (value: LeadOutcome | null): string => {
    return value || 'Unknown';
  };

  const handleSelect = (option: LeadOutcome) => {
    onChange(option);
    setOpen(false); // Close popover after selection
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-2 w-full h-8 px-3 py-1.5 text-left bg-white rounded-md focus:outline-none text-sm",
            isMobile
              ? "border border-gray-200"
              : "border border-transparent hover:border-gray-200 transition-colors"
          )}
        >
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: getOutcomeCircleColor(outcome) }}
          />
          <span className="truncate">{getDisplayText(outcome)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[350px] max-h-[400px] overflow-y-auto border border-gray-200 shadow-lg z-50"
        sideOffset={5}
        align="start"
      >
        <div className="py-0.5">
          {options.map((option, index) => (
            <div
              key={index}
              className={cn(
                "flex items-center px-3 py-1.5 cursor-pointer hover:bg-gray-50",
                outcome === option ? "bg-gray-50" : ""
              )}
              onClick={() => handleSelect(option)}
            >
              <div className="flex items-center gap-2 w-full">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: getOutcomeCircleColor(option) }}
                />
                <span className="flex-1 text-sm">{getDisplayText(option)}</span>
                {outcome === option && <Check size={14} className="text-green-600" />}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default OutcomeSelect;
